<template>
  <!-- https://vcalendar.io/examples/datepickers.html#date-range -->
  <DatePicker
    v-model="value"
    mode="date"
    :masks="masks"
    :popover="popoverProps"
    is24hr
    v-bind="$attrs"
  >
    <template #default="{ inputValue, inputEvents, isDragging }">
      <Input
        :label="label"
        :aria-label="label"
        :placeholder="placeholderFrom ?? $t('from')"
        :model-value="inputValue"
        :disabled="disabled"
        size="13"
        class="my-0"
        :class="isDragging ? 'text-gray-600' : 'text-gray-900'"
        :force-label-to-top="true"
        :variant="variant"
        :error="error"
        v-bind="$attrs"
        :input-events="inputEvents"
      >
        <template #icon>
          <CalendarIcon />
        </template>
      </Input>
    </template>
  </DatePicker>
</template>

<script lang="ts" setup>
import { useVModel } from '@vueuse/core';
import { DatePicker } from 'v-calendar';
import Input from '@components/utils/inputs/Input.vue';
import CalendarIcon from '@images/calendar.svg?component';
import { computed, onMounted, onUnmounted } from 'vue';
import type { Variant } from './Field.vue';
import type { ValidatedTree } from '@/hooks/form';

interface Props {
  modelValue: Date | null;
  masks?: {
    input?: string|string[];
    inputDateTime?: string|string[];
    inputDateTime24hr?: string|string[];
    inputTime?: string|string[];
    inputTime24hr?: string|string[];
  };
  label?: string;
  variant?: Variant;
  placeholderFrom?: string;
  placeholderTo?: string;
  error?: string | ValidatedTree | undefined;
  disabled: boolean;
}

const emit = defineEmits(['update:modelValue']);
const props = withDefaults(defineProps<Props>(), {
  masks: () => ({
    input: 'DD.MM.YYYY',
    inputTime24hr: ['HH:mm:ss', 'HH:mm'],
    inputDateTime: ['DD.MM.YYYY HH:mm:ss', 'DD.MM.YYYY HH:mm', 'DD.MM.YYYY'],
    inputDateTime24hr: ['DD.MM.YYYY HH:mm:ss', 'DD.MM.YYYY HH:mm', 'DD.MM.YYYY'],
  }),
  modelValue: null,
  disabled: false,
});

const value = useVModel(props, 'modelValue', emit, { passive: true });

const popoverProps = computed(() => ({
  visibility: props.disabled ? 'hidden' : 'click',
}));

// Apologies for the hack below, v-calendar is sh*t
const stopPopoverHide = (e) => {
  e.stopImmediatePropagation();
};

onMounted(() => {
  document.addEventListener('hide-popover', stopPopoverHide, {
    capture: true,
  });
});

onUnmounted(() => {
  document.removeEventListener('hide-popover', stopPopoverHide);
});
</script>

<style lang="scss" scoped>
:deep(.icon) {
  @apply h-3/4;

  svg {
    @apply h-full;

    path[fill] {
      @apply fill-element-neutral;
      stroke: none;
    }

    path[stroke]   {
      @apply stroke-element-neutral;
      fill: none;
    }
  }
}
</style>
